<script setup>
import { computed } from 'vue'
import ArtistOptions from '@components/artist/artist-options'
import ChannelOptions from '@components/channel/channel-options'
import GenrePills from '@components/genre/genre-pills'
import ShowOptions from '@components/show/show-options'
import formatDate from '@utils/format-date-full'

const props = defineProps({
    text: {
        type: String,
        default: '',
    },
    description: {
        type: String,
        default: '',
    },
    resource: {
        type: Object,
        default: null,
    },
    resourceType: {
        type: [String, Boolean],
        default: false,
    },
    mix: {
        type: [Boolean, Object],
        default: false,
    },
})

const type = computed(() => props.resourceType || props.resource?.type)
const airDate = computed(() =>
    type.value === 'mix' && props.resource.episode
        ? formatDate(new Date(props.resource.episode.air_start))
        : ''
)
const scheduleText = computed(() => null)
</script>

<template>
    <div class="container">
        <div
            v-if="resource && type"
            class="inner"
        >
            <div
                v-if="type === 'channel'"
                :class="['radio-dot', resource.id]"
            >
                <h5 class="radio-dot-title">{{ resource.title }}</h5>
            </div>
            <div
                v-if="type === 'channel'"
                class="main"
            >
                <BasePlaceholder
                    class="title-radio"
                    :text="resource.title"
                    :type="type"
                    tag="h2"
                />
                <BaseButtonPlay
                    :resource="resource"
                    class="button"
                    icon
                />
                <div
                    class="options"
                    @click.prevent="() => {}"
                >
                    <BaseDropdown :padded="false">
                        <BaseButton
                            slot="trigger"
                            class="trigger-button"
                            :modifiers="{ autoWidth: false, transparent: false, dots: true }"
                            button-type="tag"
                            aria-label="Options"
                            size="large"
                        />
                        <div slot="dropdown">
                            <ChannelOptions
                                :channel="resource"
                                :has-view-channel-option="false"
                                :has-settings-links="true"
                            />
                        </div>
                    </BaseDropdown>
                </div>
            </div>
            <div
                v-else-if="type === 'artist'"
                :class="['main', 'main-show']"
            >
                <h6 class="page-type">Artist Profile</h6>
                <div>
                    <BasePlaceholder
                        :text="resource.title && Number(resource.reach || 0).toLocaleString()"
                        :size="5"
                        class="total-plays"
                        tag="h5"
                    />
                    <p class="total-plays-label">Total Plays</p>
                    <BasePlaceholder
                        class="show-title"
                        :text="resource.title"
                        :type="type"
                        tag="h1"
                    />
                    <BaseButtonPlay
                        :resource="resource && resource.latestMix"
                        :class="['play-button', 'button']"
                        icon
                    />
                    <BaseFavourites
                        :artist="resource"
                        button-type="follow"
                        size="large"
                    >
                        <span slot="main">Follow</span>
                        <span slot="alt">Following</span>
                    </BaseFavourites>
                    <div
                        class="options"
                        @click.prevent="() => {}"
                    >
                        <BaseDropdown :padded="false">
                            <BaseButton
                                slot="trigger"
                                class="trigger-button"
                                :modifiers="{ autoWidth: false, transparent: false, dots: true }"
                                button-type="tag"
                                aria-label="Options"
                                size="large"
                            />
                            <div slot="dropdown">
                                <ArtistOptions
                                    :has-view-artist-option="false"
                                    :artist="resource"
                                />
                            </div>
                        </BaseDropdown>
                    </div>
                </div>
            </div>
            <div
                v-else-if="type === 'show'"
                :class="['main', 'main-show']"
            >
                <h6 class="page-type">Show Profile</h6>
                <div>
                    <BasePlaceholder
                        :text="resource.title && Number(resource.reach || 0).toLocaleString()"
                        :size="5"
                        class="total-plays"
                        tag="h5"
                    />
                    <p class="total-plays-label">Total Plays</p>
                </div>
                <BasePlaceholder
                    class="show-title"
                    :text="resource.title"
                    :type="type"
                    tag="h1"
                />
                <p
                    v-if="resource.artist"
                    class="show-artist-line"
                >
                    by
                    <BaseLink
                        v-if="resource.artist"
                        :to="{ path: `/artist/${resource.artist.url}/${resource.artist.id}/` }"
                    >
                        <div class="show-artist">{{ resource.artist.title }}</div>
                    </BaseLink>
                </p>
                <div class="genre-list">
                    <GenrePills :genres="resource.genre" />
                </div>
                <BaseButtonPlay
                    :resource="mix"
                    class="button"
                    icon
                />
                <BaseFavourites
                    :show="resource"
                    button-type="follow"
                    size="large"
                >
                    <span slot="main">Follow</span>
                    <span slot="alt">Following</span>
                </BaseFavourites>
                <span
                    v-if="scheduleText"
                    class="schedule-text"
                >{{ scheduleText }}</span>
                <div
                    class="options"
                    @click.prevent="() => {}"
                >
                    <BaseDropdown :padded="false">
                        <BaseButton
                            slot="trigger"
                            class="trigger-button"
                            :modifiers="{ autoWidth: false, transparent: false, dots: true }"
                            button-type="tag"
                            aria-label="Options"
                            size="large"
                        />
                        <div slot="dropdown">
                            <ShowOptions
                                :has-view-show-option="false"
                                :show="resource"
                            />
                        </div>
                    </BaseDropdown>
                </div>
            </div>
            <div
                v-else-if="type === 'mix'"
                :class="['main', 'main-mix']"
            >
                <BaseResourceImage
                    :resource="resource.episode"
                    :class="['image', 'image-mix']"
                    :playable-mix="resource"
                    radius="card"
                />
                <BaseLink :to="{ path: `/artist/${resource.artist.url}/${resource.artist.id}` }">
                    <BasePlaceholder
                        class="title-mix"
                        :text="resource.artist && resource.artist.title"
                        type="artist"
                        tag="h3"
                    />
                </BaseLink>
                <BasePlaceholder
                    :text="resource.show && resource.show.title"
                    type="show"
                    tag="div"
                />
                <BasePlaceholder
                    :text="airDate"
                    :size="5"
                    tag="p"
                />
                <div class="genre-list">
                    <GenrePills :genres="resource.genre" />
                </div>
            </div>
        </div>
        <div
            v-else
            class="inner"
        >
            <h6
                v-if="description"
                class="description"
            >{{ description }}</h6>
            <h1 class="title">{{ text }}</h1>
        </div>
        <BaseHeroBackground
            v-if="resource && resource.nowPlaying && resource.nowPlaying.mixes"
            :resource="resource.nowPlaying.mixes.episode"
            is-blurred
        />
        <BaseHeroBackground
            v-else-if="resource && type === 'mix' && resource.episode"
            :resource="resource.episode"
            is-blurred
        />
        <BaseHeroBackground
            v-else-if="resource && type === 'show'"
            :resource="resource"
            type="image"
        />
        <BaseHeroBackground
            v-else-if="resource && type === 'artist'"
            :resource="resource"
            :type="resource.hero ? 'hero' : 'photo'"
        />
    </div>
</template>

<style lang="scss" scoped>
@import '@design';

.inner {
    position: relative;
    z-index: $layer-page-z-index;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;

    @include container;

    @include tablet {
        flex-direction: row;
    }

    @include desktop {
        margin-top: $s-h * 0.3;
    }

    @include sidePadding;
}

.title {
    @extend %h3;

    width: 100%;
    margin-bottom: $s-xxl;
    text-align: left;

    @include tablet {
        @include h1;

        margin-top: $s-xxxl;
    }
}

.show-title {
    overflow-wrap: break-word;

    @extend %h3;

    @include desktop {
        @include h1;

        margin-bottom: $s-s;
    }
}

.description {
    position: absolute;
    top: 0;
    display: none;

    @include tablet {
        display: block;
    }
}

.title-radio {
    margin: 0 0 $size-grid-padding;
    text-align: center;

    @include tablet {
        text-align: left;
    }
}

.title-mix {
    margin: 0 0 $size-grid-padding;
    text-align: center;
    overflow-wrap: break-word;
}

.main {
    &-mix {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        margin-right: auto;
        margin-bottom: $s-xxl;
        margin-left: auto;
    }

    &-show {
        margin-right: auto;
        margin-left: auto;
        text-align: center;

        @include tablet {
            margin-left: 0;
            text-align: left;
        }
    }
}

.image {
    &-mix {
        width: 200px;
        max-width: 100%;
        margin-bottom: $s-xl;
    }
}

.container {
    position: relative;
    z-index: $layer-highlight-z-index;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding-top: $s-xl;

    @include desktop {
        min-height: 350px;
    }
}

.radio-dot {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: span(6 of 12 narrow);
    max-width: 200px;
    margin-top: gutter() * 4;
    border-radius: 50%;

    @include tablet {
        width: span(2 of 12 narrow);
        margin-top: 0;
    }

    &::after {
        position: relative;
        z-index: $layer-negative-z-index;
        display: block;
        width: 100%;
        padding-bottom: 100%;
        content: '';
        border-radius: 50%;
    }

    &.frisky::after {
        background: $c-frisky-fill;
    }

    &.chill::after {
        background: $c-chill-fill;
    }

    &.classics::after {
        background: $c-classics-fill;
    }

    &.deep::after {
        background: $c-deep-fill;
    }

    + .main {
        @include tablet {
            margin-left: gutter(of 12 narrow);
        }
    }
}

.radio-dot-title {
    display: block;
    margin: 0;

    @include center;
}

.button {
    margin-right: $s-xl;
}

.show-artist {
    display: inline-block;
    color: $c-white;

    &-line {
        margin-top: -$s-s;
    }
}

.genre-list {
    margin-bottom: $s-xl;
}

.page-type {
    display: none;

    @include tablet {
        display: block;
    }
}

.total-plays {
    margin-top: -$s-m;
    margin-bottom: 0;

    @include tablet {
        margin-top: $s-xxxl + $s-m;
    }

    &-label {
        margin-top: 0;
        margin-bottom: $s-l;

        @include tablet {
            margin-bottom: -$s-xl;
        }
    }
}

.options {
    position: relative;
    z-index: $layer-dropdown-z-index;
    display: inline-block;
    margin-left: $s-xl;
    vertical-align: top;
}

.trigger-button {
    width: 40px;
}

</style>
